import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronUp } from 'lucide-react';
import Footer from "../../../components/landing/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../../components/ui/button";

const faqs = [
  {
    category: "Booking",
    questions: [
      { q: "How do I make a booking?", a: "Browse properties on our search page, select your preferred dates and accommodation type, then click 'Reserve Now'. Fill in your guest details and proceed to payment via Razorpay." },
      { q: "Can I book without creating an account?", a: "Yes, you can make a booking as a guest. However, creating an account gives you access to booking history, easier management, and exclusive deals." },
      { q: "How far in advance can I book?", a: "You can book up to 6 months in advance for long-term stays and up to 3 months for short-term stays, subject to availability." },
      { q: "What is included in the price?", a: "The displayed price includes the base accommodation cost and GST (18%). Some properties may include utilities and amenities - check the property listing for full details." },
    ],
  },
  {
    category: "Payments",
    questions: [
      { q: "What payment methods are accepted?", a: "We accept UPI, Credit/Debit Cards, Net Banking, and popular digital wallets via our secure payment partner Razorpay." },
      { q: "Is my payment information secure?", a: "Yes. All payments are processed by Razorpay, a PCI DSS compliant payment gateway. Social Bunkr never stores your card details." },
      { q: "When will I be charged?", a: "Payment is collected at the time of booking confirmation. For long-term stays, advance rent as agreed with the host is collected." },
      { q: "Can I get a receipt or invoice?", a: "Yes. A booking confirmation with payment details is sent to your email. For GST invoices, contact our support team." },
    ],
  },
  {
    category: "Check-in & Check-out",
    questions: [
      { q: "How does check-in work?", a: "After booking confirmation, the host will share check-in instructions. Standard check-in time is 12:00 PM unless otherwise stated. You can use the app to initiate check-in on arrival." },
      { q: "What if I need to check in early or late?", a: "Contact the host through the platform to arrange early or late check-in. Additional charges may apply at the host's discretion." },
      { q: "How do I check out?", a: "Use the app or website to mark your check-out. Standard check-out time is 10:00 AM unless otherwise stated. Leave the property in the condition you found it." },
    ],
  },
  {
    category: "Cancellations & Refunds",
    questions: [
      { q: "What is the cancellation policy?", a: "Cancellation policies vary by property. The specific policy is shown on the property listing and booking confirmation page before you pay." },
      { q: "How long does a refund take?", a: "Refunds are processed within 5–7 business days to your original payment method, depending on your bank's processing time." },
      { q: "What if the host cancels my booking?", a: "If a host cancels your confirmed booking, you will receive a full refund within 3–5 business days. We will also help you find alternative accommodation." },
    ],
  },
  {
    category: "Account & Profile",
    questions: [
      { q: "How do I reset my password?", a: "Click 'Forgot Password' on the login page and enter your registered email. You'll receive a reset link within a few minutes." },
      { q: "Can I change my booking after confirmation?", a: "Modification of dates or room type depends on availability and the host's policy. Contact our support team with your booking ID for assistance." },
      { q: "How do I become a host?", a: "Sign up as a host on Social Bunkr, verify your identity, and list your property. Our team will guide you through the onboarding process." },
    ],
  },
];

const FAQItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button
        className="w-full flex items-center justify-between py-4 text-left font-medium text-foreground hover:text-primary transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>{q}</span>
        {open ? <ChevronUp className="w-5 h-5 flex-shrink-0 ml-4" /> : <ChevronDown className="w-5 h-5 flex-shrink-0 ml-4" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-4 text-foreground/70 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.02 }}>
              <Link to="/" className="text-2xl md:text-3xl font-bold">
                <span className="text-primary">Social</span>
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
              </Link>
            </motion.div>
            <nav className="hidden md:flex items-center gap-8">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="text-foreground/80 hover:text-primary font-medium transition-colors">
                  {item}
                </Link>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login"><Button variant="ghost" className="text-foreground hover:text-primary">Login</Button></Link>
              <Link to="/signup"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">Sign Up</Button></Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {isMobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden py-4 space-y-3">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="block text-foreground/80 hover:text-primary font-medium transition-colors py-2">
                  {item}
                </Link>
              ))}
            </motion.div>
          )}
        </div>
      </motion.header>

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl font-bold text-foreground mb-2">Help Center & FAQs</h1>
            <p className="text-muted-foreground mb-10">Find answers to the most common questions about Social Bunkr.</p>

            <div className="space-y-8">
              {faqs.map((section) => (
                <div key={section.category} className="bg-background rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4 text-primary">{section.category}</h2>
                  {section.questions.map((item) => (
                    <FAQItem key={item.q} q={item.q} a={item.a} />
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-10 bg-background rounded-2xl shadow-sm p-8 text-center">
              <h2 className="text-xl font-semibold text-foreground mb-2">Still have questions?</h2>
              <p className="text-muted-foreground mb-6">Our support team is ready to help you.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                    Contact Support
                  </Button>
                </Link>
                <a href="tel:+918008585553">
                  <Button variant="outline" className="rounded-full px-8">
                    Call +91-8008585553
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
